import React, { useState } from 'react';
import { SelectDialog } from './SelectDialog';
import { Select } from './Select';
import { Slider } from './Slider';
import { ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { GifPreviewCard } from './GifPreviewCard';

const VIDEO_STYLES = {
  free: [
    { value: 'not-specified', label: 'Not Specified' },
    { value: 'cinematic', label: 'Cinematic' },
    { value: 'documentary', label: 'Documentary' },
    { value: 'vlog', label: 'Vlog' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'music-video', label: 'Music Video' },
    { value: 'experimental', label: 'Experimental' },
    { value: 'aerial', label: 'Aerial' },
    { value: 'slow-motion', label: 'Slow Motion' },
    { value: 'time-lapse', label: 'Time Lapse' }
  ],
  premium: [
    { value: 'drone_shot', label: 'Drone Shot (Premium)', isPremium: true },
    { value: 'hyperreal', label: 'Hyperreal (Premium)', isPremium: true },
    { value: 'noir', label: 'Film Noir (Premium)', isPremium: true },
    { value: 'retro', label: 'Retro (Premium)', isPremium: true },
    { value: 'cyberpunk', label: 'Cyberpunk (Premium)', isPremium: true }
  ]
};

const CAMERA_MOVEMENTS = {
  free: [
    { value: 'not-specified', label: 'Not Specified' },
    { value: 'static-shot', label: 'Static' },
    { value: 'pan-shot', label: 'Pan' },
    { value: 'tilt-shot', label: 'Tilt' },
    { value: 'dolly-shot', label: 'Dolly' },
    { value: 'zoom-shot', label: 'Zoom' },
    { value: 'crane-shot', label: 'Crane' },
    { value: 'handheld-shot', label: 'Handheld' },
    { value: 'steadicam-shot', label: 'Steadicam' },
    { value: 'drone-shot', label: 'Drone' }
  ],
  premium: [
    { value: 'orbit', label: 'Orbit (Premium)', isPremium: true },
    { value: 'dutch_angle', label: 'Dutch Angle (Premium)', isPremium: true },
    { value: 'vertigo', label: 'Vertigo Effect (Premium)', isPremium: true },
    { value: 'whip_pan', label: 'Whip Pan (Premium)', isPremium: true },
    { value: 'gimbal', label: 'Gimbal (Premium)', isPremium: true }
  ]
};

const TOOLTIPS = {
  videoStyle: 'Choose the overall visual style and mood for your video content',
  cameraMovement: 'Select how the camera will move to capture your scene'
};

const VideoStyleSelect = ({ value, onChange, isPremiumUser, isUltimateUser }) => {
  // Ultimate users should have access to premium features
  const hasAccessToPremium = isPremiumUser || isUltimateUser;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedOption = [...VIDEO_STYLES.free, ...(hasAccessToPremium ? VIDEO_STYLES.premium : [])]
    .find(opt => opt.value === value) || VIDEO_STYLES.free[0];

  return (
    <div>
      <motion.div
        className="w-full group cursor-pointer"
        onClick={() => setIsDialogOpen(true)}
        whileHover="hover"
        variants={{
          hover: { y: 0 }
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[var(--textSecondary)]">
            Video Style
          </span>
          <motion.div
            variants={{
              hover: { x: 5, opacity: 1 },
              initial: { x: 0, opacity: 0 }
            }}
            initial="initial"
            className="flex items-center gap-1 text-sm text-emerald-400"
          >
            <span>Change</span>
            <ChevronRight className="w-4 h-4" />
          </motion.div>
        </div>

        <GifPreviewCard
          label={selectedOption.label}
          value={selectedOption.value}
          type="videoStyle"
          isSelected={true}
          isPremium={selectedOption.isPremium}
        />
      </motion.div>

      <SelectDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        label="Video Style"
        type="videoStyle"
        value={value}
        onChange={onChange}
        isPremiumUser={hasAccessToPremium}
        options={VIDEO_STYLES}
        tooltip={TOOLTIPS.videoStyle}
        PreviewComponent={GifPreviewCard}
      />
    </div>
  );
};

const CameraMovementSelect = ({ value, onChange, isPremiumUser, isUltimateUser }) => {
  // Ultimate users should have access to premium features
  const hasAccessToPremium = isPremiumUser || isUltimateUser;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedOption = [...CAMERA_MOVEMENTS.free, ...(hasAccessToPremium ? CAMERA_MOVEMENTS.premium : [])]
    .find(opt => opt.value === value) || CAMERA_MOVEMENTS.free[0];

  return (
    <div>
      <motion.div
        className="w-full group cursor-pointer"
        onClick={() => setIsDialogOpen(true)}
        whileHover="hover"
        variants={{
          hover: { y: 0 }
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[var(--textSecondary)]">
            Camera Movement
          </span>
          <motion.div
            variants={{
              hover: { x: 5, opacity: 1 },
              initial: { x: 0, opacity: 0 }
            }}
            initial="initial"
            className="flex items-center gap-1 text-sm text-emerald-400"
          >
            <span>Change</span>
            <ChevronRight className="w-4 h-4" />
          </motion.div>
        </div>

        <GifPreviewCard
          label={selectedOption.label}
          value={selectedOption.value}
          type="cameraMovement"
          isSelected={true}
          isPremium={selectedOption.isPremium}
        />
      </motion.div>

      <SelectDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        label="Camera Movement"
        type="cameraMovement"
        value={value}
        onChange={onChange}
        isPremiumUser={hasAccessToPremium}
        options={CAMERA_MOVEMENTS}
        tooltip={TOOLTIPS.cameraMovement}
        PreviewComponent={GifPreviewCard}
      />
    </div>
  );
};

const VideoSettings = ({ settings, onSettingChange, isPremiumUser, isUltimateUser }) => {
  // Ultimate users have access to all premium features
  const hasAccessToPremium = isPremiumUser || isUltimateUser;
  return (
    <div className="space-y-6">
      <VideoStyleSelect
        value={settings.videoStyle}
        onChange={(value) => onSettingChange('videoStyle', value)}
        isPremiumUser={isPremiumUser}
        isUltimateUser={isUltimateUser}
      />

      <CameraMovementSelect
        value={settings.cameraMovement}
        onChange={(value) => onSettingChange('cameraMovement', value)}
        isPremiumUser={isPremiumUser}
        isUltimateUser={isUltimateUser}
      />

      <div>
        <Slider
          label="Prompt Length"
          value={settings.promptLength}
          onChange={(value) => onSettingChange('promptLength', value)}
          min={1}
          max={3}
          step={1}
          labels={['Short', 'Medium', 'Long']}
        />
      </div>

      <div>
        <h3 className="text-sm font-medium text-[var(--text)] mb-2">Camera Angle</h3>
        <Select
          type="cameraAngle"
          value={settings.cameraAngle}
          onChange={(value) => onSettingChange('cameraAngle', value)}
          isPremiumUser={hasAccessToPremium}
        />
      </div>

      <div>
        <h3 className="text-sm font-medium text-[var(--text)] mb-2">Lighting</h3>
        <Select
          type="lighting"
          value={settings.lighting}
          onChange={(value) => onSettingChange('lighting', value)}
          isPremiumUser={hasAccessToPremium}
        />
      </div>
    </div>
  );
};

export default VideoSettings;